import { CallHistory } from "@/interface/dataInterface";
import axiosInstance from "@/utils/axiosInstance";
import axios from "axios";
import { addDays } from "date-fns";

const MAX_CDRS_TO_FETCH = 3000;
const DISPLAYED_LOGS_LIMIT = 30;
const API_TIMEOUT = 10000;

interface FreePBXConfig {
  server_url: string;
  client_id: string;
  client_secret: string;
  grant_type: string;
}

/**
 * Fetches call logs from FreePBX GraphQL API
 * @returns Promise with array of call history records
 * @throws Error if integration is not configured or API request fails
 */
export const fetchCallLogsFromFreePBX = async (): Promise<CallHistory[]> => {
  // Fetch FreePBX integration config
  const { data: integration } = await axiosInstance.get(
    "/integrations/find-by-key?key=freepbx",
  );
  const jsonConfig = integration?.json_value as FreePBXConfig | undefined;

  if (!jsonConfig) {
    throw new Error("FreePBX integration not configured");
  }

  if (
    !jsonConfig.server_url ||
    !jsonConfig.client_id ||
    !jsonConfig.client_secret ||
    !jsonConfig.grant_type
  ) {
    throw new Error("Missing required FreePBX configuration");
  }

  const today = new Date();
  const startDate = today.toISOString().split("T")[0];
  // Fetch logs for today and tomorrow (2 days total to capture end of day)
  const tomorrow = addDays(new Date(), 1);
  const endDate = tomorrow.toISOString().split("T")[0];

  const { server_url, client_id, client_secret, grant_type } = jsonConfig;
  const gqlUrl = `${server_url}/gql`;

  // Get OAuth token
  const bearer = await getAuthToken(
    server_url,
    grant_type,
    client_id,
    client_secret,
  );

  if (!bearer?.access_token) {
    throw new Error("Failed to obtain access token");
  }

  // Fetch CDRs from FreePBX GraphQL API
  const query = buildCDRQuery(startDate, endDate);

  const res = await axios.post(
    gqlUrl,
    { query },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer.access_token}`,
      },
      timeout: API_TIMEOUT,
    },
  );

  if (!res.data?.data?.fetchAllCdrs?.cdrs) {
    throw new Error("Invalid response from FreePBX API");
  }

  const allLogs: CallHistory[] = res.data.data.fetchAllCdrs.cdrs;

  // Deduplicate logs based on timestamp, prioritizing ANSWERED calls
  const uniqueLogs = deduplicateLogs(allLogs);

  // Sort by timestamp descending and limit results
  const sortedLogs = uniqueLogs
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, DISPLAYED_LOGS_LIMIT);

  return sortedLogs;
};

/**
 * Get OAuth token from FreePBX server
 */
async function getAuthToken(
  serverUrl: string,
  grantType: string,
  clientId: string,
  clientSecret: string,
) {
  const tokenUrl = `${serverUrl}/token`;

  const params = new URLSearchParams();
  params.append("grant_type", grantType);
  params.append("client_id", clientId);
  params.append("client_secret", clientSecret);

  const res = await axios.post(tokenUrl, params.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    timeout: API_TIMEOUT,
  });

  return res.data ?? null;
}

/**
 * Build GraphQL query for fetching CDRs
 */
function buildCDRQuery(startDate: string, endDate: string): string {
  return `query{
    fetchAllCdrs (
        first : ${MAX_CDRS_TO_FETCH}
        after : 0
        orderby : date
        startDate: "${startDate}"
        endDate: "${endDate}"
    )
    {
        cdrs {
        id
            uniqueid
            calldate
            timestamp
            clid
            src
            dst
            dcontext
            channel
            dstchannel
            lastapp
            lastdata
            duration
            billsec
            disposition
            accountcode
            userfield
            did
            recordingfile
            cnum
            outbound_cnum
            outbound_cnam
            dst_cnam
            linkedid
            peeraccount
            sequence
            amaflags
        }
        totalCount
        status
        message
    }
    }`;
}

/**
 * Deduplicate logs based on timestamp, prioritizing ANSWERED calls
 */
function deduplicateLogs(logs: CallHistory[]): CallHistory[] {
  return Array.from(
    logs.reduce((map, item) => {
      const existing = map.get(item.timestamp);
      if (
        !existing ||
        (item.disposition === "ANSWERED" &&
          existing.disposition !== "ANSWERED")
      ) {
        map.set(item.timestamp, item);
      }
      return map;
    }, new Map()).values(),
  );
}
